import { useState, useEffect, useMemo } from "react";
import useSWR from "swr";
import useDealer from "./useDealer";
import ReportService from "services/ReportService";
import useDealersSwr from "hooks/useDealersSwr";
import { useBackdrop } from "hooks/useBackdrop";

const labelMapping = {
    uniqueVisitors: "Unique Visitors",
    customerMessages: "Customer Messages",
    customerCalls: "Customer Calls",
    customerCarts: "Customer Carts",
    visitsToCart: "Visits to Cart",
    visitsToOrder: "Visits to Order",
    visitsToMessage: "Visits to Message",
    visitsToCalls: "Visits to Calls",
    totalOrders: "Total Orders",
    ordersCanceled: "Orders Canceled",
    fulfillmentRatio: "Fulfillment Ratio",
    numberOfDaysInReport: "Number of Days in Report",
    averageOrdersPerDay: "Average Orders Per Day",
    cartsCreated: "Carts Created",
    conversion: "Conversion",
    ordersCompleted: "Orders Completed",
    orderPendingOnOrder: "Order Pending/On-order",
    averageItemsPerOrder: "Average Items Per Order",
    numberOfOrdersAdjusted: "Number of Orders Adjusted",
    grossSiteSalesGenerated: "Gross Site Sales Generated",
    netSales: "Net Sales",
    netSalesShipped: "Net Sales Shipped",
    salesCanceledLost: "Sales Canceled/ Lost",
    salesPendingOnOrder: "Sales Pending/On-Order",
    netDeclinedSales: "Net Declined Orders ($)",
    totalSales: "Total Sales",
    averageSalesPerDay: "Average Sales per Day",
    averageSale: "Average Sale",
    averageRetainedMargin: "Average Retained Margin",
    collectedShipping: "Collected Shipping",
    accessory: "Accessory",
    replacement: "Replacement",
    other: "Other",
    totalOrdersWithTax: "Total Orders with Tax",
    totalTaxCollected: "Total Tax Collected",
    taxRate: "Tax Rate",
    totalNetProfits: "Total Net Profits",
    averageProfitPerSale: "Average Profit per Sale",
    totalLostProfits: "Total Lost Profits",
    dealerProfits: "Dealer Profits",
    retainedDealerMargin: "Retained Dealer Margin",
    percentOfNetProfit: "% of Net Profit",
    commissionsPaid: "Commissions Paid",
    retainedCommMargin: "Retained Comm Margin",
};

const useSalesReport = () => {
    const { dealers = [] } = useDealersSwr();
    const { id: dealerId } = useDealer();
    const [selectedCountry, setSelectedCountry] = useState("USA");
    const { openBackdrop, closeBackdrop } = useBackdrop();

    const [selectedOption, setSelectedOption] = useState("this_week");
    const [customDateRange, setCustomDateRange] = useState({
        startDate: "",
        endDate: "",
    });

    // 1. Fetch Date Range based on selectedOption
    const { data: rangeData } = useSWR(
        selectedOption ? ["report-date-range", selectedOption] : null,
        () => ReportService.fetchDateRange(selectedOption).then((res) => res.data),
        { revalidateOnFocus: false }
    );

    useEffect(() => {
        if (rangeData) {
            setCustomDateRange({
                startDate: rangeData.startDate,
                endDate: rangeData.endDate,
            });
        }
    }, [rangeData]);

    const formattedDealerId = useMemo(() => {
        if (dealerId === 0) {
            if (dealers.length === 0) return "";
            return dealers
                .filter((d) => !selectedCountry || d.country === selectedCountry)
                .map((d) => d.id)
                .join(",");
        }
        return dealerId;
    }, [dealerId, dealers, selectedCountry]);

    // 2. Fetch Sales Summary Report using SWR
    const {
        data: response,
        isLoading,
        isValidating,
        mutate,
    } = useSWR(
        formattedDealerId && customDateRange.startDate && customDateRange.endDate
            ? [
                "sales-summary",
                formattedDealerId,
                customDateRange.startDate,
                customDateRange.endDate,
                selectedCountry,
            ]
            : null,
        () =>
            ReportService.fetchSalesSummaryReport(
                formattedDealerId,
                "",
                customDateRange.startDate,
                customDateRange.endDate
            ).then((res) => res.data),
        { revalidateOnFocus: false, keepPreviousData: true }
    );

    useEffect(() => {
        if (isLoading || isValidating) {
            openBackdrop();
        } else {
            closeBackdrop();
        }
    }, [isLoading, isValidating]);

    const transformData = (conceptData = {}) => {
        return Object.keys(conceptData).map((key) => ({
            label: labelMapping[key] || key,
            value: conceptData[key],
            key: key,
        }));
    };

    const salesReportData = useMemo(() => {
        if (!response) {
            return {
                engagements: [],
                closingRatio: [],
                totalOrders: 0,
                ordersCanceled: 0,
                fulfillmentRatio: 0,
                netSalesPercentage: 0,
                netSalesAmount: 0,
                orderPerformance: [],
                salesPerformance: [],
                shippingPerformance: [],
                catalogPerformance: [],
                tax: [],
                profits: [],
            };
        }

        return {
            engagements: transformData(response.engagements),
            closingRatio: transformData(response.closingRatio),
            totalOrders: response.totalOrders,
            ordersCanceled: response.ordersCanceled,
            fulfillmentRatio: response.fulfillmentRatio,
            netSalesPercentage: response.netSalesPercentage,
            netSalesAmount: response.netSalesAmount,
            orderPerformance: transformData(response.salesSummary.orderPerformance),
            salesPerformance: transformData(response.salesSummary.salesPerformance),
            shippingPerformance: transformData(response.salesSummary.shippingPerformance),
            catalogPerformance: transformData(response.salesSummary.catalogPerformance),
            tax: transformData(response.salesSummary.tax),
            profits: transformData(response.salesSummary.profits),
        };
    }, [response]);

    const handleFilterByDateRangeClick = () => {
        mutate();
    };

    return {
        selectedOption,
        setSelectedOption,
        customDateRange,
        setCustomDateRange,
        salesReportData,
        handleFilterByDateRangeClick,
        dealerId,
        dealers,
        selectedCountry,
        setSelectedCountry,
    };
};

export default useSalesReport;
